import { CustomerShell } from './CustomerShell'

/**
 * Sign-in, and the whole of it: a phone number and a button. There is no signup
 * screen anywhere in this app, deliberately - a customer exists because Mitali
 * knows them, and a public form that mints customer rows is how the system being
 * replaced accumulated hundreds of nameless shells.
 *
 * The three outcomes a customer can hit are all spelled out here rather than
 * collapsed into one "check your WhatsApp": a bad number is their typo to fix, a
 * rate limit is a wait with a number of minutes on it, and success says which
 * channel the link went to so nobody sits watching the wrong app.
 */
export type SignInStatus =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; phoneMasked: string; reissued: boolean }
  | { kind: 'bad_phone' }
  | { kind: 'rate_limited'; retryAfterMinutes: number }
  | { kind: 'error'; code: string | null }

export interface SignInProps {
  phone: string
  onPhoneChange?: (v: string) => void
  status: SignInStatus
  onRequest?: () => void
}

export function SignIn({ phone, onPhoneChange, status, onRequest }: SignInProps) {
  const sending = status.kind === 'sending'

  return (
    <CustomerShell
      title="Sign in"
      creditPaise={null}
      cart={null}
      primaryAction={{
        label: sending ? 'Sending...' : 'Send me a link',
        disabled: sending || phone.trim().length === 0,
        onPress: onRequest,
      }}
    >
      <p className="sf-lede">
        Enter the number Mitali has for you. We send a link - no password, no signup.
      </p>

      <label className="field">
        <span className="t-label">Phone number</span>
        <input
          className="field-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => onPhoneChange?.(e.target.value)}
        />
      </label>

      <div aria-live="polite" className="sf-signin-status">
        {status.kind === 'sent' ? (
          <p className="async async-ok">
            Link sent to {status.phoneMasked} on WhatsApp.
            {status.reissued ? ' Your earlier link no longer works.' : ''}
          </p>
        ) : null}
        {status.kind === 'bad_phone' ? (
          <p className="async async-error" role="alert">
            That does not look like a phone number. Ten digits, or +91 and ten digits.
          </p>
        ) : null}
        {status.kind === 'rate_limited' ? (
          <p className="async async-error" role="alert">
            Too many links requested. Try again in {status.retryAfterMinutes} minutes.
          </p>
        ) : null}
        {status.kind === 'error' ? (
          <p className="async async-error" role="alert">
            Could not send the link. Tap to try again.{status.code ? ` (${status.code})` : ''}
          </p>
        ) : null}
      </div>
    </CustomerShell>
  )
}

/** Never render a full number back at a customer on a shared device. */
export function maskPhone(e164: string): string {
  return e164.length <= 4 ? e164 : `${e164.slice(0, 3)}...${e164.slice(-4)}`
}

export function retryAfterMinutes(ms: number): number {
  return Math.max(1, Math.ceil(ms / 60000))
}
